// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import FactoryIcon from '@mui/icons-material/Factory'
import LaptopIcon from '@mui/icons-material/Laptop'
import WebIcon from '@mui/icons-material/Web'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export default function FormatsSection() {
  const { t } = useTranslation('servicesOtAwarenessTraining')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const iconSx = { fontSize: '1.9rem', color: themeColors.bg.start }

  const formats: { icon: ReactElement; title: string; text: string }[] = [
    {
      icon: <FactoryIcon sx={iconSx} />,
      title: t('formats.items.01.title'),
      text: t('formats.items.01.text')
    },
    {
      icon: <LaptopIcon sx={iconSx} />,
      title: t('formats.items.02.title'),
      text: t('formats.items.02.text')
    },
    {
      icon: <WebIcon sx={iconSx} />,
      title: t('formats.items.03.title'),
      text: t('formats.items.03.text')
    }
  ]

  // Three cards, so no two-column step - it would leave an orphan on the row.
  const getGrid = () => {
    if (w > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('formats.eyebrow')}
        title={t('formats.title')}
        intro={[t('formats.intro')]}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {formats.map(format => (
          <Box
            key={format.title}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              background: themeColors.kroemtech.background,
              border: `1px solid ${themeColors.gray.lighter}`,
              borderRadius: '10px',
              padding: '1.75rem',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ lineHeight: 0, marginBottom: '1.1rem' }}>
              {format.icon}
            </Box>

            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: '1.02rem',
                fontWeight: 700,
                lineHeight: 1.3,
                color: themeColors.kroemtech.dunkelblau,
                marginBottom: '0.7rem'
              }}
            >
              {format.title}
            </Typography>

            <Typography
              sx={{
                fontSize: getFontBodySmall(),
                lineHeight: 1.65,
                color: themeColors.gray.defaultText
              }}
            >
              {format.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
